import BrandLogo from "@/components/brand-logo";
import { Footer } from "@/features/landing/components/footer";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import { useEffect } from "react";

export const Route = createFileRoute("/legal")({
  component: LegalPage,
});

/**
 * Placeholder marker for details that are not yet settled (legal entity, ABN,
 * contact addresses). Rendered visibly so a draft can never be mistaken for a
 * published policy.
 */
function Tbc({ children }: { children: React.ReactNode }) {
  return (
    <span className="bg-warning-subtle text-warning-ink rounded px-1.5 py-0.5 font-mono text-[0.8em] font-semibold">
      [TO BE CONFIRMED — {children}]
    </span>
  );
}

const LAST_UPDATED = "3 September 2026";

const sections = [
  { id: "privacy", title: "Privacy Policy" },
  { id: "terms", title: "Terms of Service" },
  { id: "consent-guide", title: "Client Consent Guide" },
  { id: "security", title: "Security Standards" },
];

function SectionHeading({ id, title }: { id: string; title: string }) {
  return (
    <h2
      id={id}
      className="border-border text-foreground scroll-mt-24 border-b pb-3 text-2xl font-extrabold tracking-tight"
    >
      {title}
    </h2>
  );
}

function H3({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-foreground mt-6 mb-2 text-base font-bold">
      {children}
    </h3>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-muted-foreground mb-3 text-sm leading-relaxed">
      {children}
    </p>
  );
}

function UL({ children }: { children: React.ReactNode }) {
  return (
    <ul className="text-muted-foreground mb-3 ml-5 list-disc space-y-1.5 text-sm leading-relaxed">
      {children}
    </ul>
  );
}

function LegalPage() {
  // TanStack Router does not scroll to a hash on initial load, so a direct hit
  // on /legal#security would otherwise land at the top of the page.
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    // Defer past first paint so the target element exists.
    const raf = requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="bg-background text-foreground min-h-screen font-sans antialiased">
      <header className="border-border/80 bg-background/95 sticky top-0 z-50 border-b backdrop-blur-md">
        <div className="container mx-auto flex h-16 items-center justify-between px-6">
          <Link to="/" className="flex items-center space-x-3">
            <BrandLogo className="size-7" />
            <span className="text-foreground text-xl font-bold tracking-tight">
              Solu Health
            </span>
          </Link>
          <Link
            to="/"
            className="text-muted-foreground hover:text-foreground text-sm font-medium transition-colors"
          >
            Back to home
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        <div className="mx-auto max-w-3xl">
          <h1 className="text-foreground text-3xl font-extrabold tracking-tight">
            Legal &amp; Privacy
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            Last updated {LAST_UPDATED}
          </p>

          {/* Draft banner — deliberately prominent and non-dismissible. */}
          <div className="border-warning/25 bg-warning-subtle mt-6 flex gap-3 rounded-lg border p-4">
            <AlertTriangle className="text-warning-ink mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <h2 className="text-warning-ink text-sm font-bold">
                Draft — pending legal review
              </h2>
              <p className="text-warning-ink mt-1 text-sm leading-relaxed">
                These documents are working drafts prepared from the platform's
                technical architecture. They have{" "}
                <strong>
                  not been reviewed by an Australian privacy lawyer
                </strong>{" "}
                and do not yet constitute Solu Health's published privacy policy
                or binding terms. Items marked{" "}
                <span className="font-mono text-xs">[TO BE CONFIRMED]</span>{" "}
                require confirmation before publication.
              </p>
            </div>
          </div>

          {/* Table of contents */}
          <nav className="border-border bg-card mt-8 rounded-lg border p-5">
            <h2 className="text-muted-foreground mb-3 text-xs font-semibold tracking-wider uppercase">
              Contents
            </h2>
            <ol className="space-y-2">
              {sections.map((s, i) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-primary hover:text-primary/80 text-sm font-medium hover:underline"
                  >
                    {i + 1}. {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="mt-12 space-y-14">
            {/* ---------------------------------------------- Privacy Policy */}
            <section>
              <SectionHeading id="privacy" title="1. Privacy Policy" />

              <div className="mt-5">
                <P>
                  Solu Health is a clinical documentation platform for
                  Australian psychology practices, operated by{" "}
                  <Tbc>legal entity name</Tbc> <Tbc>ABN / ACN</Tbc> of{" "}
                  <Tbc>registered address</Tbc> ("we", "us"). This policy
                  explains how we handle personal information, including health
                  information, under the <em>Privacy Act 1988</em> (Cth) and the
                  Australian Privacy Principles (APPs).
                </P>
                <P>
                  Your treating practitioner or their practice is the health
                  service provider responsible for your clinical records. We
                  process that information on their behalf as their software
                  provider.
                </P>

                <H3>What we collect</H3>
                <UL>
                  <li>
                    <strong>Practitioner account information</strong> — name,
                    email address, organisation membership and role,
                    authentication credentials, and billing details.
                  </li>
                  <li>
                    <strong>Client information</strong> entered by a
                    practitioner — identifying details, contact information, and
                    consent records.
                  </li>
                  <li>
                    <strong>Health information</strong> — session recordings,
                    transcripts, clinical notes, assessment responses and
                    scores, and documents. Health information is{" "}
                    <em>sensitive information</em> under the Privacy Act and
                    attracts the highest level of protection.
                  </li>
                  <li>
                    <strong>Technical and audit records</strong> — access logs
                    and audit events recording who accessed which records and
                    when.
                  </li>
                </UL>

                <H3>Why we collect it (APP 3 and APP 6)</H3>
                <P>
                  We collect and use this information for the primary purpose of
                  providing the platform: transcribing sessions, drafting
                  clinical documentation for practitioner review, storing
                  clinical records, administering accounts, and processing
                  subscriptions. We do not use health information for direct
                  marketing.
                </P>
                <P>
                  <strong>
                    Client data is never used to train, retrain, or improve
                    artificial intelligence models
                  </strong>
                  , whether ours or a third party's. Our AI processing
                  agreements expressly prohibit model training on submitted
                  content.
                </P>

                <H3>Notification of collection (APP 5)</H3>
                <P>
                  Where information is collected about a client, the collecting
                  practitioner is responsible for notifying that client of the
                  matters in APP 5, including that a session may be recorded and
                  processed using automated transcription and AI-assisted
                  drafting. The platform provides a{" "}
                  <Link
                    to="/consent/info"
                    className="text-primary font-medium hover:underline"
                  >
                    client-facing disclosure
                  </Link>{" "}
                  and a digital consent workflow for this purpose.
                </P>

                <H3>Where your data is held</H3>
                <P>
                  All primary infrastructure and the system of record are hosted
                  in Australia, in the Google Cloud region{" "}
                  <code className="bg-muted rounded px-1 py-0.5 text-xs">
                    australia-southeast1
                  </code>{" "}
                  (Sydney). This covers the clinical database, session audio,
                  real-time notification data, backups, secrets, and logs. Audio
                  transcription is performed by Microsoft Azure Speech in{" "}
                  <code className="bg-muted rounded px-1 py-0.5 text-xs">
                    australiaeast
                  </code>{" "}
                  (Sydney), and AI summarisation by Google Vertex AI in
                  Australia.
                </P>

                <H3>Overseas disclosure (APP 8)</H3>
                <P>
                  Two service providers hold data outside Australia. We disclose
                  this plainly because APP 8 makes us accountable for how
                  overseas recipients handle your information:
                </P>
                <UL>
                  <li>
                    <strong>Stripe</strong> (United States) — payment and
                    subscription processing. Stripe receives practitioner
                    billing and account details.{" "}
                    <strong>
                      It does not receive client health information.
                    </strong>
                  </li>
                  <li>
                    <strong>Brevo</strong> (European Union, France) —
                    transactional email, including consent requests, portal
                    invitations, and support correspondence. Brevo receives
                    recipient email addresses and message content.
                  </li>
                </UL>
                <P>
                  No client health records are transferred outside Australia as
                  part of ordinary platform operation. Terms governing overseas
                  disclosure of sensitive information are{" "}
                  <Tbc>
                    pending legal review — APP 8 position and consent wording
                  </Tbc>
                  .
                </P>

                <H3>Access and correction (APP 12 and APP 13)</H3>
                <P>
                  If you are a client, requests to access or correct your health
                  records should be directed to your treating practitioner or
                  practice, who holds those records and is best placed to apply
                  the exceptions that apply to health information. Practitioners
                  can access and correct their own account information in the
                  application, or contact us at <Tbc>privacy contact email</Tbc>
                  .
                </P>

                <H3>Data breaches</H3>
                <P>
                  We maintain access logging and audit records to support breach
                  detection. Where a data breach is likely to result in serious
                  harm, it is an eligible data breach under Part IIIC of the
                  Privacy Act, and we will notify the Office of the Australian
                  Information Commissioner (OAIC) and affected individuals, in
                  coordination with the relevant practice, as required by the
                  Notifiable Data Breaches scheme.
                </P>

                <H3>Retention</H3>
                <P>
                  Session audio is deleted immediately once transcription
                  completes. Clinical records are retained for the period the
                  practitioner's professional and legal obligations require —
                  health records legislation in most Australian jurisdictions
                  requires adult records to be kept for at least seven years
                  from last contact, and for minors until age 25. Archived
                  client records are retained until their retention period
                  expires before permanent destruction is possible.
                </P>

                <H3>Complaints</H3>
                <P>
                  Privacy complaints can be made to{" "}
                  <Tbc>complaints contact</Tbc> and will be acknowledged within
                  a reasonable period. If you are not satisfied with our
                  response, you may complain to the OAIC at oaic.gov.au.
                </P>
              </div>
            </section>

            {/* ------------------------------------------- Terms of Service */}
            <section>
              <SectionHeading id="terms" title="2. Terms of Service" />

              <div className="mt-5">
                <H3>The service</H3>
                <P>
                  Solu Health provides software for recording, transcribing, and
                  drafting clinical documentation. It is a documentation tool.
                  It is not a medical device, does not provide clinical advice,
                  and does not make or contribute to clinical decisions.
                </P>

                <H3>Eligibility</H3>
                <P>
                  Accounts are for registered health practitioners and their
                  practices. By creating an account you confirm that you hold
                  current registration with the relevant Australian regulatory
                  body and that you are authorised to bind your practice to
                  these terms.
                </P>

                <H3>Your responsibilities</H3>
                <UL>
                  <li>
                    You are the health service provider. You control your client
                    records and are responsible for meeting your own privacy,
                    record-keeping, and professional obligations.
                  </li>
                  <li>
                    You must obtain informed consent from each client before
                    recording a session or processing their information through
                    the platform. See the{" "}
                    <a
                      href="#consent-guide"
                      className="text-primary font-medium hover:underline"
                    >
                      Client Consent Guide
                    </a>
                    .
                  </li>
                  <li>
                    You must review, correct, and approve every AI-generated
                    draft before it becomes part of a clinical record.
                  </li>
                  <li>
                    You are responsible for keeping your account credentials
                    secure and for the activity of users you invite to your
                    organisation.
                  </li>
                </UL>

                <H3>AI-assisted drafting</H3>
                <P>
                  Transcription and summarisation are automated and{" "}
                  <strong>will contain errors</strong>. Output is a draft
                  requiring clinician review. You remain the author of, and
                  clinically responsible for, every note in your records. We
                  make no warranty as to the clinical accuracy, completeness, or
                  fitness of generated content.
                </P>

                <H3>Subscriptions and billing</H3>
                <P>
                  Paid plans are billed in advance through Stripe on the cycle
                  selected at checkout, and renew automatically until cancelled.
                  Cancellation takes effect at the end of the current billing
                  period. Fees already paid are not refundable except where
                  required by the Australian Consumer Law.
                </P>

                <H3>Australian Consumer Law</H3>
                <P>
                  Nothing in these terms excludes, restricts, or modifies any
                  guarantee, right, or remedy you have under the Australian
                  Consumer Law that cannot lawfully be excluded. Where liability
                  can be limited, our liability is limited to resupplying the
                  service or paying the cost of resupply.
                </P>

                <H3>Termination and data export</H3>
                <P>
                  You may cancel at any time. Before your account closes you can
                  export your clinical records, and we recommend doing so —
                  retaining client records after you stop using the platform
                  remains your professional obligation. We may suspend an
                  account for non-payment or for use that breaches these terms.
                </P>

                <H3>Governing law</H3>
                <P>
                  These terms are governed by the laws of{" "}
                  <Tbc>state or territory</Tbc>, Australia.
                </P>
              </div>
            </section>

            {/* -------------------------------------- Client Consent Guide */}
            <section>
              <SectionHeading
                id="consent-guide"
                title="3. Client Consent Guide"
              />

              <div className="mt-5">
                <P>
                  This guide is for practitioners. It sets out what to cover
                  when seeking a client's consent to session recording and
                  AI-assisted documentation. It is guidance, not legal advice —
                  your professional body's requirements take precedence.
                </P>

                <H3>What informed consent needs to cover</H3>
                <UL>
                  <li>
                    That the session will be <strong>audio recorded</strong>,
                    and that the recording is deleted as soon as it has been
                    transcribed.
                  </li>
                  <li>
                    That the recording is converted to a transcript by an
                    automated transcription service, and that the transcript is
                    processed by an AI model to draft clinical notes.
                  </li>
                  <li>
                    That <strong>you review and approve</strong> every draft,
                    and that the AI makes no decisions about their care.
                  </li>
                  <li>
                    Where the data is held — in Australia — and that their
                    information is not used to train AI models.
                  </li>
                  <li>
                    That consent is <strong>entirely voluntary</strong>. A
                    client may decline recording, AI drafting, or both, and
                    still receive the same care.
                  </li>
                  <li>
                    That consent can be <strong>withdrawn at any time</strong>,
                    and that withdrawal stops future sessions being recorded or
                    summarised.
                  </li>
                </UL>

                <H3>Getting consent in the platform</H3>
                <P>
                  Send a consent request to the client by email or SMS before
                  their session. The client reviews a plain-language disclosure
                  — the same one published at{" "}
                  <Link
                    to="/consent/info"
                    className="text-primary font-medium hover:underline"
                  >
                    /consent/info
                  </Link>{" "}
                  — and records their decision. Consent status appears on the
                  client record and on your dashboard.
                </P>
                <P>
                  Consent is stored as an <strong>append-only history</strong>:
                  each grant or withdrawal is written as a new record capturing
                  the consent type, the decision, the method, who acted, and the
                  version of the disclosure shown. Earlier decisions are never
                  overwritten, so the record shows what the client agreed to and
                  when.
                </P>

                <H3>Withdrawal</H3>
                <P>
                  A client may withdraw consent by telling you directly or
                  through the client portal. Record the withdrawal against their
                  client record. Withdrawal is prospective — it stops further
                  recording and AI drafting. Notes already in the clinical
                  record are retained, because your record-keeping obligations
                  continue to apply to them.
                </P>

                <H3>Practical notes</H3>
                <UL>
                  <li>
                    Confirm consent verbally at the start of a recorded session
                    even where written consent is already on file.
                  </li>
                  <li>
                    For clients under 18, or where capacity is in question,
                    follow your professional guidelines on who can consent.
                  </li>
                  <li>
                    If a third party is present in the session, their consent to
                    recording is required too.
                  </li>
                </UL>
              </div>
            </section>

            {/* ---------------------------------------- Security Standards */}
            <section>
              <SectionHeading id="security" title="4. Security Standards" />

              <div className="mt-5">
                <P>
                  This section describes the controls that are implemented in
                  the platform today. It is a factual summary, not a
                  certification claim — Solu Health does not currently hold an
                  independent security certification.
                </P>

                <H3>Encryption</H3>
                <UL>
                  <li>
                    All traffic is encrypted in transit over TLS, and all data
                    stores are encrypted at rest.
                  </li>
                  <li>
                    Client clinical content is additionally{" "}
                    <strong>envelope encrypted</strong> at the application
                    layer: each client record has its own data encryption key,
                    which is wrapped by a key belonging to that practice and
                    held in Google Cloud Key Management Service. One practice's
                    keys cannot decrypt another's data.
                  </li>
                </UL>

                <H3>Access control</H3>
                <UL>
                  <li>
                    Every request is authenticated and scoped to a single
                    organisation. Data belonging to one practice is not
                    reachable from another.
                  </li>
                  <li>
                    Access to individual client records is further controlled
                    per client, with owner, editor, and viewer roles, so access
                    within a practice can be limited to the treating team.
                  </li>
                  <li>
                    Multi-factor authentication is supported, via authenticator
                    apps and passkeys.
                  </li>
                  <li>
                    The clinical database has no public IP address and no stored
                    password. Services authenticate to it using short-lived
                    cloud identity tokens.
                  </li>
                </UL>

                <H3>Audit logging</H3>
                <P>
                  Access to and modification of clinical records is recorded in
                  an organisation-scoped audit log, supporting investigation of
                  suspected unauthorised access.
                </P>

                <H3>Data handling</H3>
                <UL>
                  <li>
                    Session audio is deleted from storage immediately after
                    transcription completes.
                  </li>
                  <li>
                    Clinical notes retain full version history, so changes are
                    traceable and deletions are recoverable.
                  </li>
                  <li>
                    Deleting a client archives the record rather than destroying
                    it; permanent destruction is restricted to practice owners
                    and blocked until the retention period has expired.
                  </li>
                  <li>
                    <strong>No client data is used to train AI models.</strong>
                  </li>
                </UL>

                <H3>Backups and availability</H3>
                <P>
                  Production data is backed up with point-in-time recovery
                  enabled. Formal recovery-time and recovery-point objectives
                  are <Tbc>to be documented</Tbc>.
                </P>

                <H3>Reporting a vulnerability</H3>
                <P>
                  If you believe you have found a security vulnerability, please
                  report it to <Tbc>security contact email</Tbc>. We ask that
                  you give us a reasonable opportunity to remediate before
                  public disclosure.
                </P>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
